import type { Container, Sprite } from 'pixi.js';
import type { RecyclablePiece } from './RecyclableCullingView';
import { GAME_OBJECT_POOL } from '../../../game/GameConstants';

interface WinStreakPathNodeViewPropsConfig {
  pathLayer: Container;
  x: number;
  y: number;
  checkMarkType: WinStreakPathNodeCheckType;
}

export enum WinStreakPathNodeCheckType {
  None,
  Yellow,
  Green,
}

export class WinStreakPathNodeView implements RecyclablePiece {
  private topSprite: Sprite | null = null;

  private checkSprite: Sprite | null = null;

  private readonly offsetY = 6;

  private checkMarkType: WinStreakPathNodeCheckType;

  constructor(private readonly config: WinStreakPathNodeViewPropsConfig) {
    this.checkMarkType = this.config.checkMarkType;
  }

  public async changeCheckMark(type: WinStreakPathNodeCheckType): Promise<void> {
    const prevCheckmark = this.checkMarkType;

    // dont override green checks
    if (prevCheckmark === WinStreakPathNodeCheckType.Green) return;

    this.checkMarkType = type;
    if (!this.topSprite || !this.checkSprite) return;

    if (prevCheckmark !== WinStreakPathNodeCheckType.None) {
      await this.checkSprite.tweenTo({ scaleXY: 0 }, 200, 'sinIn', true).asyncOnComplete();
    }

    this.updateCheckMarkSprite();

    await this.checkSprite.tweenTo({ scaleXY: 1 }, 200, 'sinOut', true).asyncOnComplete();
  }

  public activate(): void {
    const { pathLayer, x, y } = this.config;

    this.topSprite = GAME_OBJECT_POOL.getSprite().props({
      parent: pathLayer,
      texture: 'Hexagon_Path',
      anchor: 0.5,
      x,
      y: y + this.offsetY,
    });

    this.checkSprite = GAME_OBJECT_POOL.getSprite().props({
      parent: pathLayer,
      texture: 'Checked_Tile_Green',
      anchor: 0.5,
      x,
      y: y + 1,
    });
    this.updateCheckMarkSprite();
  }

  private updateCheckMarkSprite(): void {
    if (!this.checkSprite) return;

    if (this.checkMarkType === WinStreakPathNodeCheckType.None) {
      this.checkSprite.props({
        visible: false,
      });
    } else {
      this.checkSprite.props({
        texture: this.checkMarkType === WinStreakPathNodeCheckType.Green ? 'Checked_Tile_Green' : 'Checked_Tile_Yellow',
        visible: true,
      });
    }
  }

  public deactivate(): void {
    if (!this.topSprite) return;
    GAME_OBJECT_POOL.addSprite(this.topSprite, this.checkSprite);
    this.topSprite = null;
    this.checkSprite = null;
  }

  public destroy(): void {
    this.deactivate();
  }
}
